// Self-service sign-up (Phase 17.D). Mirrors SignIn's two-column editorial layout.
// POSTs /auth/register, then shows an "email sent" done panel. The response is
// always a generic 202 (anti-enumeration), so the done panel is identical whether
// or not the email already existed.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, type FormEvent } from 'react';

import { EntityDisclaimer } from '../../components/EntityDisclaimer';

import { Honeypot, TurnstileWidget } from '../../lib/turnstile';
import { useNavigate } from 'react-router-dom';

import { Brand, Kicker } from '../../components/shell';
import { register } from '../../lib/auth';

export default function SignUp() {
  const navigate = useNavigate();
  const [firmName, setFirmName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [website, setWebsite] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await register(firmName.trim(), email.trim(), password, '', '', {
        website, ...(captcha ? { cf_turnstile_response: captcha } : {}),
      });
      setDone(true);
    } catch (err: any) {
      const detail = err?.payload?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not create your account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)' }}>
      <header style={{ padding: '24px var(--pad-x)', borderBottom: 'var(--hairline)' }}>
        <div style={{ maxWidth: 'var(--max)', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Brand onClick={() => navigate('/')} />
        </div>
      </header>

      <main
        style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'stretch', minHeight: 0 }}
        className="signin-grid"
      >
        {/* Left — editorial column */}
        <section style={{
          padding: '80px var(--pad-x)',
          borderRight: 'var(--hairline)',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <div>
            <Kicker>01 · Get Started</Kicker>
            <h1 className="page-title" style={{ maxWidth: '14ch', margin: '0 0 32px' }}>
              Run your <em>fund</em> on Rev9.
            </h1>
            <p className="serif-body" style={{ maxWidth: '42ch', margin: 0 }}>
              Create your workspace, connect your accounts, and set your fund terms.
              Daily reconciliation and monthly statements, from day one.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 64 }}>
            <hr className="rule" />
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24 }}>
              <span className="label">YOUR WORKSPACE</span>
              <span className="serif-body" style={{ fontSize: 16, color: 'var(--ink-2)' }}>
                A private, isolated workspace for your firm — yours alone.
              </span>
            </div>
            <hr className="rule" />
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24 }}>
              <span className="label">SECURED</span>
              <span className="serif-body" style={{ fontSize: 16, color: 'var(--ink-2)' }}>
                Email verification, then two-factor authentication on every sign-in.
              </span>
            </div>
            <hr className="rule" />
          </div>
        </section>

        {/* Right — form / done column */}
        <section style={{
          padding: '80px var(--pad-x)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ width: '100%', maxWidth: 420 }}>
            {done ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                <Kicker>CHECK YOUR EMAIL</Kicker>
                <h2 className="page-title" style={{ margin: 0, fontSize: 32 }}><em>Almost there.</em></h2>
                <p className="serif-body" style={{ margin: 0, fontSize: 16, color: 'var(--ink-2)' }}>
                  If that email is available, we have sent a verification link to confirm
                  your account. Click it to finish setting up your fund.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                  <button className="btn" onClick={() => navigate('/sign-in')}>
                    To sign in <span className="btn-arrow">→</span>
                  </button>
                  <button className="btn-link" onClick={() => navigate('/verify-email')}>
                    Resend link
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                <Kicker>CREATE YOUR ACCOUNT</Kicker>

                <div className="field">
                  <label className="field-label">Firm / fund name</label>
                  <input className="field-input" type="text" value={firmName}
                         onChange={e => setFirmName(e.target.value)} autoComplete="organization" required autoFocus />
                </div>

                <div className="field">
                  <label className="field-label">Work email</label>
                  <input className="field-input" type="email" value={email}
                         onChange={e => setEmail(e.target.value)} autoComplete="email" required />
                </div>

                <div className="field">
                  <label className="field-label">Password</label>
                  <input className="field-input" type="password" value={password}
                         onChange={e => setPassword(e.target.value)} autoComplete="new-password"
                         minLength={14} required />
                  {/* OB.4 — state the rule up front; matches the server's
                      MinimumLengthValidator (min_length=14). minLength gives an
                      inline browser block before any round-trip; the server still
                      enforces the common/numeric/similarity rules, surfaced via
                      the error line below. */}
                  <p className="field-help" style={{ marginTop: 8 }}>
                    At least 14 characters. Avoid common or all-number passwords.
                  </p>
                </div>

                {error && (
                  <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15, color: 'var(--accent)' }}>
                    {error}
                  </div>
                )}

                <Honeypot value={website} onChange={setWebsite} />
                <TurnstileWidget onToken={setCaptcha} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 8 }}>
                  <button type="submit" className="btn" disabled={submitting}>
                    {submitting ? 'Creating…' : <>Create account <span className="btn-arrow">→</span></>}
                  </button>
                  <button type="button" className="btn-link" onClick={() => navigate('/sign-in')}>
                    Have an account? Sign in
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>

      <footer style={{
        padding: '20px var(--pad-x)', borderTop: 'var(--hairline)',
        fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: 'var(--ink-3)',
        display: 'flex', justifyContent: 'space-between',
      }}>
        {/* adviser-status label removed — LC-REV9-001 sweep via BH-RAPP-001 */}
        <span>Rev9 Capital · Secured session</span>
        <span>Miami, FL</span>
      </footer>
      <div style={{ padding: '0 var(--pad-x) 20px' }}>
        <EntityDisclaimer style={{ margin: 0 }} />
      </div>

      <style>{`
        @media (max-width: 900px) {
          .signin-grid { grid-template-columns: 1fr !important; }
          .signin-grid > section:first-child { border-right: 0; border-bottom: var(--hairline); }
        }
      `}</style>
    </div>
  );
}
